import { SpecialAnchorTypes, ToolBaseTypes, ToolTypes } from '@labradorsports/constants';
import { arraySlidingWindow } from '@labradorsports/utils';

import { Field, FieldPiece, FieldViewport, FrameSnapshot, Move } from '../models/index.js';

import {
    applyOffset,
    distance,
    findClosestLineSegment,
    findClosestPointOnLineSegment,
    findDistanceAlongStraightMovement,
    getBezierControlPoints,
    getBezierDistanceFunction,
    getCoordsOffset,
    getPointAlongBezier,
    moveAlongStraightMovement,
    moveTowardsPointDistance,
    pointsAreClose,
    sortByDistance,
} from './geometry.js';
import { createLines } from './play.js';

export function defaultMove(field: Field, target: FieldPoint | FieldPiece, type: string, targetId?: number): Move {
    const origin = target instanceof FieldPiece ? target.origin : target;
    const mv = new Move({
        type,
        target: targetId,
        origin: { ...origin },
    });

    mv.anchors = [
        {
            x: origin.x + 5,
            y: origin.y - 5,
        },
    ].map((anc: any) => {
        return field.clampCoords(anc);
    });

    return mv;
}

export function standstillMove(origin: Point) {
    return new Move({
        origin: { ...origin },
        type: ToolTypes.MOVE,
        anchors: [{ ...origin }],
    });
}

export function getMoveLines(config: PlayConfigSpec, lines: AnchorLine[], frameIdx?: number) {
    return lines.filter((line) => {
        if (!('move' in line)) {
            return false;
        }

        if (config.MoveConfig[line.move.type].baseType !== ToolBaseTypes.MOVE) {
            return false;
        }

        return typeof frameIdx !== 'number' || typeof line.frameIdx !== 'number' || frameIdx === line.frameIdx;
    });
}

export function calculatePartialMovement(move: Move, distanceAlong: number): Point {
    if (!move) {
        return null;
    }

    if (move.anchors.length === 1) {
        return moveTowardsPointDistance(move.origin, move.anchors[0], distanceAlong);
    }

    const points = move.curved ? getCurvedMovePoints(move) : [move.origin, ...move.anchors];
    const [leg, remainingDistance] = moveAlongStraightMovement(points, distanceAlong);
    const startPoint = points[leg - 1];
    const endPoint = points[leg];

    return moveTowardsPointDistance(startPoint, endPoint, remainingDistance);
}

/** Point immediately before `anchorId` along a move-shaped path (its origin, then its anchors in order). */
export function getPreviousMoveAnchor(move: { origin: Point; anchors: Point[] }, anchorId: number): Point {
    return anchorId === 0 ? move.origin : move.anchors[anchorId - 1];
}

export function calculatePercentageMovement(move: Move, percentage: number): Point {
    return calculatePartialMovement(move, getMoveLength(move) * percentage);
}

export function getCurvedMoveLegs(origin: Point, anchors: Point[]) {
    const { correctedPoints, correctedControlPoints } = getBezierControlPoints([origin, ...anchors]);

    return arraySlidingWindow(correctedPoints, (a, c, idx) => {
        const relativeControlPoint = getCoordsOffset(origin, correctedControlPoints[idx]);

        const relativeA = getCoordsOffset(origin, a);
        const relativeC = getCoordsOffset(origin, c);

        const [length] = getBezierDistanceFunction(relativeA, relativeControlPoint, relativeC);

        return {
            a: relativeA,
            c: relativeC,
            controlPoint: relativeControlPoint,
            length,
        };
    });
}

export function getMoveLength(move: Move) {
    if (move.curved) {
        const legs = getCurvedMoveLegs(move.origin, move.anchors);

        return legs.reduce((acc, leg) => acc + leg.length, 0);
    }

    const distances = arraySlidingWindow([move.origin, ...move.anchors], distance);
    return distances.reduce((acc, dist) => acc + dist, 0);
}

export function getCurvedMovePoints(move: Move): Point[] {
    const numPoints = 100;
    const legs = getCurvedMoveLegs(move.origin, move.anchors);

    const points = [];

    legs.forEach((leg) => {
        for (let i = 0; i <= numPoints; i++) {
            points.push(applyOffset(move.origin, getPointAlongBezier(leg.a, leg.controlPoint, leg.c, i / numPoints)));
        }
    });

    return points;
}

export function sliceMove(move: Move, start: number, end: number): Move {
    const points = move.curved ? getCurvedMovePoints(move) : [move.origin, ...move.anchors];
    const [startLeg, startDistance] = moveAlongStraightMovement(points, start);
    const [endLeg, endDistance] = moveAlongStraightMovement(points, end);

    const origin = moveTowardsPointDistance(points[startLeg - 1], points[startLeg], startDistance);
    const endPoint = moveTowardsPointDistance(points[endLeg - 1], points[endLeg], endDistance);
    const anchors = points.slice(startLeg, endLeg).concat([endPoint]);

    return new Move({
        ...move.toObj(),
        // lengthRatio: 1 is to correctly handle pass animations - is it appropriate for it to be in this function?
        lengthRatio: 1,
        curved: false,
        origin,
        anchors,
    });
}

export function combineMoves(moves: Move[]) {
    const firstMove = moves[0];

    return new Move({
        ...firstMove.toObj(),
        anchors: moves.reduce((acc, move) => {
            if (acc.length > 0 && pointsAreClose(acc[acc.length - 1], move.anchors[0])) {
                acc.pop();
            }

            return [...acc, ...move.anchors];
        }, []),
    });
}

/**
 * Returns the pass moves and pauses for a pass
 * Returns a consistent set of moves to match the consistent set of timings returned by calculatePassTimings
 * In order, the moves are:
 *  - the carrier movement
 *  - the pre-pass pause
 *  - the pass
 *  - the post-pass pause
 *  - the receiver movement
 * @param carrierMove - The carrier move
 * @param pass - The pass move
 * @param receiverMove - The receiver move
 * @returns [prePass, prePassPause, pass, postPassPause, postPass]
 */
export function calculatePassMoves(carrierMove: Move, pass: Move, receiverMove?: Move): [Move, Move, Move, Move, Move] {
    const prePass = carrierMove ? sliceMove(carrierMove, 0, pass.props.start ?? 0) : standstillMove(pass.origin);
    const prePassPause = standstillMove(pass.origin);

    const postPass = receiverMove
        ? sliceMove(receiverMove, pass.props.end ?? getMoveLength(receiverMove), getMoveLength(receiverMove))
        : standstillMove(pass.anchors[pass.anchors.length - 1]);
    const postPassPause = standstillMove(postPass.origin);

    prePass.id = 0;
    prePassPause.id = 1;
    postPassPause.id = 3;
    postPass.id = 4;

    return [prePass, prePassPause, pass, postPassPause, postPass];
}

/**
 * Returns the timings for a pass
 * Returns a consistent number of timings to match the consistent set of moves returned by calculatePassMoves
 * In order, the timings are:
 *  - 0 for all scenarios
 *  - the end time of the carrier movement
 *  - the end time of the pre-pass pause
 *  - the end time of the pass
 *  - the end time of the post-pass pause
 *  - the end time of the receiver movement
 * @param longestMoveLength - The length of the longest move
 * @param carrierMove - The carrier move
 * @param pass - The pass move
 * @param receiverMove - The receiver move
 * @returns [0, prePassStart, prePassEnd, passStart, passEnd, postPassStart, postPassEnd]
 */
export function calculatePassTimings(
    longestMoveLength: number,
    carrierMove: Move,
    pass: Move,
    receiverMove?: Move
): [number, number, number, number, number, number] {
    const handoffPass = Boolean(carrierMove && receiverMove);
    const receiverMoveLength =
        receiverMove && getMoveLength(receiverMove) > 0 ? getMoveLength(receiverMove) : longestMoveLength;
    const carrierMoveLength = carrierMove ? getMoveLength(carrierMove) : null;
    const start = pass.props?.start ?? 0;
    const end = pass.props?.end ?? receiverMoveLength;
    const passLength = getMoveLength(pass);

    const prePassStart = 0;
    let prePassEnd = 0;
    let passStart = 0;
    let passEnd = 0;
    let postPassStart = 0;
    let postPassEnd = 0;

    const processTimings = (timings: [number, number, number, number, number, number]) => {
        return timings.map((n) => Math.min(n / longestMoveLength, 1)) as [
            number,
            number,
            number,
            number,
            number,
            number
        ];
    };

    if (handoffPass) {
        if (end >= receiverMoveLength) {
            // If pass end is at the end of receiver move, we can start the pass at any time
            prePassEnd = start;
            passStart = Math.min(longestMoveLength - passLength / 4, start);
            passEnd = longestMoveLength;
            postPassStart = longestMoveLength;
            postPassEnd = longestMoveLength;
        } else if (start >= carrierMoveLength) {
            // If pass start is at the end of carrier move, the carrier can hold the ball for a time
            prePassEnd = start;
            passStart = end - passLength / 4;
            passEnd = end;
            postPassStart = end;
            postPassEnd = receiverMoveLength;
        } else {
            prePassEnd = start;
            passStart = Math.min(end - passLength / 4, start);
            passEnd = end;
            postPassStart = end;
            postPassEnd = receiverMoveLength;
        }
    } else if (carrierMove) {
        prePassEnd = start;
        passStart = start;
        passEnd = Math.min(start + passLength / 4, longestMoveLength);
        postPassStart = passEnd;
        postPassEnd = longestMoveLength;
    } else if (receiverMove) {
        prePassEnd = Math.max(end - passLength / 4, 0);
        passStart = Math.max(end - passLength / 4, 0);
        passEnd = end;
        postPassStart = end;
        postPassEnd = receiverMoveLength;
    } else {
        prePassEnd = 0;
        passStart = 0;
        passEnd = passLength / 4;
        postPassStart = longestMoveLength;
        postPassEnd = longestMoveLength;
    }

    return processTimings([prePassStart, prePassEnd, passStart, passEnd, postPassStart, postPassEnd]);
}

export function calculateTimingBasedPercentage(timings: number[], segments: Move[], percentage: number): number {
    const shiftedTimings = timings.slice(1).concat([1]);
    const moveLengths = segments.map((segment) => getMoveLength(segment));
    const totalLength = moveLengths.reduce((acc, length) => acc + length, 0);

    const segmentIndex = shiftedTimings.findIndex((timing) => percentage <= timing) + 1;

    const relevantSegments = moveLengths.slice(0, segmentIndex);

    let idx = 0;
    let newPercentage = 0;
    while (idx < relevantSegments.length) {
        const length = relevantSegments[idx];
        const timing = shiftedTimings[idx];

        if (idx === segmentIndex - 1) {
            const previousTiming = shiftedTimings[idx - 1] ?? 0;
            const timingThisSegment = timing - previousTiming;
            const remainingTiming = percentage - previousTiming;

            const percentageThisSegment = (length / totalLength) * (remainingTiming / timingThisSegment);
            newPercentage += percentageThisSegment;
        } else {
            newPercentage += length / totalLength;
        }

        idx++;
    }

    return newPercentage;
}

export function findClosestPointAlongMove(move: Move, point: Point): { closestPoint: Point; distanceAlong: number } {
    if (move.curved) {
        const points = getCurvedMovePoints(move).map((pt, i) => ({ ...pt, i }));
        const sortedPoints = sortByDistance(point, points);
        const closestPoint = sortedPoints[0];
        let distanceAlong = 0;
        let totalDist = 0;

        arraySlidingWindow(points, (a, b, idx) => {
            const dist = distance(a, b);
            if (idx < closestPoint.i) {
                distanceAlong += dist;
            }

            totalDist += dist;
        });

        return {
            closestPoint,
            distanceAlong,
        };
    }

    const closestLine = findClosestLineSegment(point, createLines(move));
    const closestPoint = findClosestPointOnLineSegment(point, closestLine);

    const distanceAlong = findDistanceAlongStraightMovement([move.origin, ...move.anchors], closestPoint);

    return { closestPoint, distanceAlong };
}

export function findClosestPassablePoint(
    lines: AnchorLine[],
    fieldViewport: FieldViewport,
    frame: FrameSnapshot,
    offset: FieldPoint,
    anchorType: string,
    ballId: number
) {
    const carrierMove = frame.getMove(frame.getCarrier(ballId).id);
    const closestPassable = frame.findClosestPassable(offset, anchorType, [], ballId);

    if (!closestPassable) {
        return {
            closestDistance: Infinity,
        };
    }

    const closestDistance = distance(closestPassable.origin, offset);
    const modifiedLines = lines.map((line) => fieldViewport.getFieldLine(line));

    const closestLine = findClosestLineSegment(
        offset,
        modifiedLines,
        modifiedLines.filter((line) => line.move === carrierMove)
    );

    if (!closestLine) {
        return {
            closestDistance,
            closestPassable,
        };
    }

    const closestMove = closestLine.move;

    const { closestPoint, distanceAlong } = findClosestPointAlongMove(closestMove, offset);
    const closestLineDistance = distance(offset, closestPoint);

    return {
        closestLineDistance,
        closestLine,
        closestDistance,
        closestPassable,
        closestPoint,
        closestMove,
        distanceAlong,
    };
}

export function findClosestOptionPoint(
    lines: AnchorLine[],
    fieldViewport: FieldViewport,
    frame: FrameSnapshot,
    offset: FieldPoint,
    sourceId: number
) {
    const sourceMove = frame.getMove(sourceId);
    const closestOption = frame.findClosestOption(offset, sourceId);

    if (!closestOption) {
        return {
            closestDistance: Infinity,
        };
    }

    const closestDistance = distance(closestOption.origin, offset);
    const modifiedLines = lines.map((line) => fieldViewport.getFieldLine(line));

    const closestLine = findClosestLineSegment(
        offset,
        modifiedLines,
        modifiedLines.filter((line) => line.move === sourceMove)
    );

    if (!closestLine) {
        return {
            closestDistance,
            closestOption,
        };
    }

    const closestMove = closestLine.move;

    const { closestPoint, distanceAlong } = findClosestPointAlongMove(closestMove, offset);
    const closestLineDistance = distance(offset, closestPoint);

    return {
        closestLineDistance,
        closestLine,
        closestDistance,
        closestOption,
        closestPoint,
        closestMove,
        distanceAlong,
    };
}

export function getPassEndLimit(pass: Move, closestMove: Move, frame: FrameSnapshot) {
    const carrierMove = frame.getMove(frame.getCarrier(pass.props.ball).id);
    const movesLengthRatio = (carrierMove?.lengthRatio ?? 1) / (closestMove?.lengthRatio ?? 1);

    return (pass?.props?.start ?? 0) * movesLengthRatio;
}

/**
 * Returns player ids associated with a move
 */
export function getMovePlayerIds(move: Move, frame: FrameSnapshot, MoveConfig: MoveConfigType): number[] {
    const ids = [];

    if (MoveConfig[move.type]?.baseType == ToolBaseTypes.PASS) {
        if (typeof move.props?.targetId === 'number') {
            ids.push(move.props.targetId);
        } else {
            ids.push(move.target);
        }

        if (typeof move.props?.ball === 'number') {
            const carrier = frame.getCarrier(move.props.ball);
            if (carrier) ids.push(carrier.id);
        }
    } else if (MoveConfig[move.type]?.baseType === ToolBaseTypes.SHOOT) {
        const goalie = frame.goalies.find((g) => g.props?.pieceId === move.target);

        if (goalie) {
            ids.push(goalie.id);
        }
    } else {
        ids.push(move.target, move.source);
    }

    return ids.filter((id): id is number => typeof id === 'number');
}

/**
 * Resolves the move for an anchor (e.g. drag subject).
 */
export function getMoveFromAnchor(
    anchor: { type: string; id?: number; target?: number },
    frame: FrameSnapshot,
    MoveConfig: MoveConfigType
): Move | undefined {
    const baseType = MoveConfig[anchor.type]?.baseType;

    if (
        anchor.type === ToolTypes.MOVE ||
        anchor.type === ToolTypes.DMOVE ||
        anchor.type === ToolTypes.OMOVE ||
        anchor.type === SpecialAnchorTypes.MOVE_ELBOW
    ) {
        if (typeof anchor.target === 'number') return frame.getMove(anchor.target);
    } else if (baseType === ToolBaseTypes.PASS || anchor.type === SpecialAnchorTypes.PASS_START) {
        if (typeof anchor.id === 'number') return frame.passes?.find((p: any) => p.id === anchor.id);
    } else if (baseType === ToolBaseTypes.SHOOT) {
        if (typeof anchor.id === 'number') return frame.shots?.find((s: any) => s.id === anchor.id);
    } else if (anchor.type === ToolTypes.OPTION || anchor.type === SpecialAnchorTypes.OPTION_START) {
        if (typeof anchor.id === 'number') return frame.options?.find((o: any) => o.id === anchor.id);
    } else if (anchor.type === ToolTypes.ASSIGN) {
        if (typeof anchor.id === 'number') return frame.assignments?.find((a: any) => a.id === anchor.id);
    }

    return undefined;
}
